import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, MoreHorizontal } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { AmountInput } from '@/components/amount-input';
import { Checkbox, ErrorBanner } from '@/components/form';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  BUDGET_SECTIONS,
  copyAcross,
  monthLabel,
  spreadEvenly,
  sumPlain,
  useBudget,
  type Budget,
} from '@/lib/accountant';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount } from '@/lib/format';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/accounting/budgets/$budgetId')({ component: BudgetEditor });

const plain = (v: string) => (toMinor(v) === 0n ? '' : v);

/** Screen A11 (document 06): budget grid by account and month. */
function BudgetEditor() {
  const { budgetId } = Route.useParams();
  const budget = useBudget(budgetId);
  if (budget.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (budget.isError)
    return <EmptyState title="Could not load the budget" hint={budget.error.message} />;
  return <Editor key={`${budget.data.id}-${budget.data.version}`} budget={budget.data} />;
}

function Editor({ budget }: { budget: Budget }) {
  const orgId = useOrgId();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isStaff = me.data?.isStaff ?? false;
  const initial = useMemo(
    () => Object.fromEntries(budget.rows.map((r) => [r.accountId, r.amounts.map(plain)])),
    [budget],
  );
  const [values, setValues] = useState<Record<string, string[]>>(initial);
  const [name, setName] = useState(budget.name);
  const [allRows, setAllRows] = useState(budget.rows.every((r) => r.total === '0.00'));
  useEffect(() => setValues(initial), [initial]);
  const changed = budget.rows.filter(
    (r) => (values[r.accountId] ?? []).join('|') !== (initial[r.accountId] ?? []).join('|'),
  );
  const dirty = changed.length > 0 || name.trim() !== budget.name;
  const save = useMutation({
    mutationFn: () =>
      api<Budget>(`/orgs/${orgId}/budgets/${budget.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          version: budget.version,
          ...(name.trim() !== budget.name ? { name: name.trim() } : {}),
          rows: changed.map((r) => ({ accountId: r.accountId, amounts: values[r.accountId] })),
        }),
      }),
    onSuccess: async (b) => {
      await qc.invalidateQueries({ queryKey: ['budgets', orgId] });
      toast(`Budget "${b.name}" saved.`);
    },
  });
  const set = (accountId: string, next: string[]) =>
    setValues((v) => ({ ...v, [accountId]: next }));
  const sectionTotal = (keys: string[]) =>
    budget.months.map((_, i) =>
      budget.rows
        .filter((r) => keys.includes(r.category))
        .reduce((s, r) => s + (toMinor(values[r.accountId]?.[i] ?? '') ?? 0n), 0n),
    );
  const income = sectionTotal(['income', 'other_income']);
  const costs = sectionTotal(['cost_of_sales', 'expenses', 'other_expense']);
  const net = income.map((v, i) => v - costs[i]!);
  const sum = (v: bigint[]) => v.reduce((a, b) => a + b, 0n);

  return (
    <div>
      <Link
        to="/accounting/budgets"
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="size-4" /> Budgets
      </Link>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-sm font-medium" htmlFor="budget-name">
            Budget name
          </label>
          <Input
            id="budget-name"
            className="mt-1 w-80 text-lg font-semibold"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isStaff}
          />
        </div>
        <p className="pb-2 text-sm text-muted-foreground">{budget.fiscalYearLabel} · by month</p>
        <Checkbox
          className="pb-2"
          label="Show accounts without amounts"
          checked={allRows}
          onChange={(e) => setAllRows(e.target.checked)}
        />
        <div className="ml-auto flex gap-2 pb-1">
          <Button
            variant="outline"
            onClick={() =>
              void navigate({
                to: '/reports/$reportKey',
                params: { reportKey: 'budget-vs-actuals' },
                search: { budget: budget.id, from: budget.months[0]!, columns: 'month' },
              })
            }
          >
            Budget vs actuals
          </Button>
          {isStaff ? (
            <>
              <Button variant="outline" disabled={!dirty} onClick={() => setValues(initial)}>
                Revert
              </Button>
              <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      <ErrorBanner>{save.error?.message ?? null}</ErrorBanner>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-[1600px] text-sm" data-testid="budget-grid">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="sticky left-0 z-10 w-64 bg-card px-3 py-2">Account</th>
              {budget.months.map((m) => (
                <th key={m} className="px-1 py-2 text-right">
                  {monthLabel(m)}
                </th>
              ))}
              <th className="px-3 py-2 text-right">Total</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {BUDGET_SECTIONS.map((s) => {
              const rows = budget.rows.filter(
                (r) =>
                  r.category === s.key &&
                  (allRows ||
                    (values[r.accountId] ?? []).some((v) => v !== '') ||
                    (initial[r.accountId] ?? []).some((v) => v !== '')),
              );
              if (!rows.length) return null;
              const total = sectionTotal([s.key]);
              return (
                <Fragment key={s.key}>
                  <tr className="border-b bg-muted/40">
                    <td
                      className="sticky left-0 bg-muted/40 px-3 py-1.5 font-semibold"
                      colSpan={14}
                    >
                      {s.title}
                    </td>
                    <td />
                  </tr>
                  {rows.map((r) => {
                    const v = values[r.accountId] ?? Array(12).fill('');
                    return (
                      <tr key={r.accountId} className="border-b">
                        <td className="sticky left-0 bg-card px-3 py-1 pl-6">
                          {r.code} {r.name}
                        </td>
                        {v.map((x, i) => (
                          <td key={i} className="px-1 py-1">
                            <AmountInput
                              aria-label={`${r.name} ${monthLabel(budget.months[i]!)}`}
                              className="h-8 w-[104px] rounded border border-transparent bg-transparent px-1 text-right hover:border-input focus:border-ring"
                              value={x}
                              disabled={!isStaff}
                              onValueChange={(nv) =>
                                set(
                                  r.accountId,
                                  v.map((y, j) => (j === i ? nv : y)),
                                )
                              }
                            />
                          </td>
                        ))}
                        <td className="tnum px-3 py-1 text-right font-medium">
                          {formatAmount(sumPlain(v))}
                        </td>
                        <td>
                          {isStaff ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label={`Row actions for ${r.name}`}
                                >
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onSelect={() => set(r.accountId, copyAcross(v).map(plain))}
                                >
                                  Copy first month across
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() =>
                                    set(r.accountId, spreadEvenly(sumPlain(v)).map(plain))
                                  }
                                >
                                  Spread total evenly
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() => set(r.accountId, Array(12).fill(''))}
                                >
                                  Clear row
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-b font-semibold">
                    <td className="sticky left-0 bg-card px-3 py-1.5">Total {s.title}</td>
                    {total.map((t, i) => (
                      <td key={i} className="tnum px-2 py-1.5 text-right">
                        {formatMinor(t)}
                      </td>
                    ))}
                    <td className="tnum px-3 py-1.5 text-right">{formatMinor(sum(total))}</td>
                    <td />
                  </tr>
                </Fragment>
              );
            })}
            <tr className="border-t-2 border-double font-semibold">
              <td className="sticky left-0 bg-card px-3 py-2">Net income</td>
              {net.map((t, i) => (
                <td key={i} className="tnum px-2 py-2 text-right">
                  {formatMinor(t)}
                </td>
              ))}
              <td className="tnum px-3 py-2 text-right">{formatMinor(sum(net))}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
